import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { cn } from '../cn.ts';

export type ToastTone = 'info' | 'success' | 'error';

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** Milliseconds; 0 keeps it until dismissed. Default 4000 (7000 for errors). */
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'tone' | 'duration'>> {
  id: number;
  title: ReactNode;
  description?: ReactNode;
}

interface ToastContextValue {
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * In-app toasts. Inside the dashboard prefer `hostActions.toast()` for short confirmations (they
 * appear in the dashboard's own toast area); use these for richer or app-local messages.
 */
export function ToastProvider({ children, max = 3 }: { children: ReactNode; max?: number }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback(
    (id: number) => setItems((current) => current.filter((item) => item.id !== id)),
    [],
  );
  const toast = useCallback(
    ({ title, description, tone = 'info', duration }: ToastInput) => {
      const id = nextId.current++;
      const item: ToastItem = {
        id,
        title,
        description,
        tone,
        duration: duration ?? (tone === 'error' ? 7000 : 4000),
      };
      setItems((current) => [...current, item].slice(-max));
      return id;
    },
    [max],
  );
  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('Wrap the app in <ToastProvider>');
  return value;
}

const ICONS: Record<ToastTone, ReactNode> = {
  info: <Info className="size-4 text-n-slate-11" aria-hidden="true" />,
  success: <CircleCheck className="size-4 text-n-teal-10" aria-hidden="true" />,
  error: <CircleAlert className="size-4 text-n-ruby-10" aria-hidden="true" />,
};

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  useEffect(() => {
    if (item.duration <= 0) return;
    const timer = setTimeout(() => onDismiss(item.id), item.duration);
    return () => clearTimeout(timer);
  }, [item.id, item.duration, onDismiss]);

  return (
    <div
      role={item.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex items-start gap-3 w-full max-w-sm px-4 py-3 rounded-xl shadow-lg animate-toast-in',
        'bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container',
      )}
    >
      <span className="mt-0.5">{ICONS[item.tone]}</span>
      <div className="flex flex-col min-w-0 grow gap-0.5">
        <p className="m-0 text-heading-3 text-n-slate-12">{item.title}</p>
        {item.description && <p className="m-0 text-body-main text-n-slate-11">{item.description}</p>}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => onDismiss(item.id)}
        className="shrink-0 -me-1 p-0.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2 cursor-pointer"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/** The toast region; rendered by ToastProvider. */
export function Toaster({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4"
    >
      {items.map((item) => (
        <ToastView key={item.id} item={item} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
